import { z } from 'zod';

/**
 * In-app help topics — the panel's built-in documentation system.
 *
 * Every module (built-in or out-of-tree) can contribute help topics:
 * self-contained markdown pages describing what the module provides, where
 * it is configured and how it is used. The help center renders them inside
 * the app for staff and — for topics that opt in — inside the client
 * portal, so no user ever needs access to the source repository to read
 * the documentation. Topics of disabled modules disappear from the index,
 * exactly like their navigation items.
 */

/** Who a topic is written for: panel operators, portal tenants, or both. */
export const HELP_AUDIENCES = ['staff', 'client', 'both'] as const;
export type HelpAudience = (typeof HELP_AUDIENCES)[number];

/** Topic slugs are URL path segments: lowercase, digits, dashes. */
export const HELP_SLUG_PATTERN = /^[a-z][a-z0-9-]*$/;

/**
 * A help topic as a module declares it. The body is markdown in the
 * renderer's supported subset: headings, paragraphs, bold/italic/inline
 * code, fenced code blocks, lists, tables, blockquotes, links and rules.
 * Raw HTML is never rendered.
 */
export interface ModuleHelpTopic {
  /** Unique within the module; the topic renders at /help/<moduleKey>/<slug>. */
  slug: string;
  title: string;
  /** One-line summary shown on the help index and searched with the body. */
  summary: string;
  /** Markdown body (see above for the supported subset). */
  body: string;
  /** Who sees the topic (default `staff`). `client` topics render in the portal. */
  audience?: HelpAudience;
  /** Index section the topic is grouped under (default: the module's name). */
  category?: string;
  /** Sort weight within the category, lower first (default 100). */
  order?: number;
}

/** Validates an out-of-tree module's `help` contribution before it loads. */
export const moduleHelpTopicSchema = z.object({
  slug: z.string().regex(HELP_SLUG_PATTERN, 'slug must be lowercase and URL-safe (a-z, 0-9, -)'),
  title: z.string().min(1).max(200),
  summary: z.string().min(1).max(500),
  body: z.string().min(1).max(200_000),
  audience: z.enum(HELP_AUDIENCES).optional(),
  category: z.string().min(1).max(100).optional(),
  order: z.number().int().optional(),
});

export const moduleHelpSchema = z.array(moduleHelpTopicSchema);

/** An index entry — everything the help center list needs, without the body. */
export interface HelpTopicSummaryDto {
  /** Module that contributed the topic. */
  moduleKey: string;
  slug: string;
  title: string;
  summary: string;
  category: string;
  order: number;
}

/** A full topic, as the reader page fetches it. */
export interface HelpTopicDto extends HelpTopicSummaryDto {
  /** Markdown body. */
  body: string;
}
